import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import type { EmbedPluginProps } from './embed';
import { DEVICE_SIZE, mq } from '../../utils/mq';
import ViewCompact from '../../icons/viewCompact';

const Wrapper = styled.div`
  min-height: 300px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      min-height: 600px;
    `,
  )}
`;

const TwitterBlockquote = styled.blockquote`
  height: 270px;
  max-width: 558px;
  margin: auto;
  background: var(--color-alt-2);
  a {
    color: var(--color-alt-2);
  }
`;

const WrapperEmpty = styled.div`
  padding-top: calc(50% / (16 / 9) - 60px);
  padding-bottom: calc(50% / (16 / 9) - 60px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

declare global {
  interface Window {
    twttr: {
      widgets: {
        load: Function;
      };
    };
    TikTokEmbeds?: {
      process(): void;
    };
    bluesky?: {
      scan(): void;
    };
  }
}

interface TweetProps {
  src: string;
}

interface GoogleDocsProps {
  src: string;
}

interface BlueSkyProps {
  src: string;
}

const loadTwitter = () => {
  if (window.twttr) {
    window.twttr.widgets.load();
  } else {
    const script = document.createElement('script');
    script.async = true;
    script.setAttribute('src', 'https://platform.twitter.com/widgets.js');
    document.getElementsByTagName('body')[0].appendChild(script);
  }
};

function TwitterTweetContainer({ src }: TweetProps) {
  useEffect(() => {
    loadTwitter();
  }, []);

  const oldSrc = src.replace(/https:\/\/x.com/, 'https://twitter.com');

  return (
    <div className="twitter-embed">
      <TwitterBlockquote className="twitter-tweet tw-align-center">
        <a href={oldSrc}>...</a>
      </TwitterBlockquote>
    </div>
  );
}

const loadTiktok = () => {
  if (window.TikTokEmbeds) {
    window.TikTokEmbeds.process();
  } else {
    const script = document.createElement('script');
    const elems = document.getElementsByTagName('script')[0];
    script.id = 'tiktok-async';
    script.async = true;
    script.src = 'https://www.tiktok.com/embed.js';
    elems.parentNode.insertBefore(script, elems);
  }
};

function TikTokContainer({ src }: TweetProps) {
  const [tiktok, setTikTok] = useState({
    id: '',
    title: '',
    authorHref: '',
    author: '',
  });
  useEffect(() => {
    loadTiktok();
    fetch(`https://www.tiktok.com/oembed?url=${src}`)
      .then((response) => response.json())
      .then((data) => {
        setTikTok({
          id: data.embed_product_id,
          title: data.title,
          authorHref: data.author_url,
          author: data.author_unique_id,
        });
      });
  }, [src]);

  return (
    <div key={`tiktok-${tiktok.id}`}>
      <blockquote className="tiktok-embed" cite={src} data-video-id={tiktok.id} data-embed-from="oembed">
        <section>
          <a target="_blank" title={tiktok.title} href={tiktok.authorHref} rel="noreferrer">
            {tiktok.author}
          </a>
          <p>{tiktok.title}</p>
        </section>
      </blockquote>
    </div>
  );
}

const loadBlueSky = () => {
  if (window?.bluesky) {
    window.bluesky.scan();
  } else {
    const script = document.createElement('script');
    const elems = document.getElementsByTagName('script')[0];
    script.id = 'bluesky-async';
    script.async = true;
    script.charset = 'utf-8';
    script.src = 'https://embed.bsky.app/static/embed.js';
    elems.parentNode.insertBefore(script, elems);
  }
};

function BlueSkyContainer({ src }: BlueSkyProps) {
  const [blueSky, setBlueSky] = useState({
    bsky_id: '',
    author_name: '',
    author_url: '',
    cache_age: 0,
    height: null,
    html: '',
    script: '',
    provider_name: '',
    provider_url: '',
    version: '',
    width: 0,
  });

  useEffect(() => {
    fetch(`https://embed.bsky.app/oembed?url=${src}`)
      .then((response) => response.json())
      .then((data) => {
        if (data?.html) {
          const pattern = /<script[^>]*>(.*?)<\/script>/s;
          const match = data.html.match(pattern);

          if (match) {
            data.script = match[0];
          }
          data.html = data.html.replace(pattern, '');

          const patternId = /data-bluesky-cid="([^"]+)"/;
          const matchId = data.html.match(patternId);
          if (matchId) {
            data.bsky_id = matchId;
          }
        }

        if (data?.bsky_id) {
          loadBlueSky();

          setBlueSky({
            bsky_id: data.bsky_id,
            author_name: data.author_name,
            author_url: data.author_url,
            cache_age: data.cache_age,
            height: data.height,
            html: data.html,
            script: data.script,
            provider_name: data.provider_name,
            provider_url: data.provider_url,
            version: data.version,
            width: data.width,
          });
        }
      });
  }, [src]);

  if (blueSky?.html !== '') {
    return <div key={`bluesky-${blueSky.bsky_id}`} dangerouslySetInnerHTML={{ __html: blueSky.html }} />;
  }

  return null;
}

function GoogleDocsContainer({ src }: GoogleDocsProps) {
  return (
    <div className="google-docs-embed">
      <iframe
        title="Google Form"
        src={src}
        width="100%"
        height="950"
        frameBorder="0"
        marginHeight={0}
        marginWidth={0}
      />
    </div>
  );
}

function TwitterTimelineContainer({ src }: TweetProps) {
  useEffect(() => {
    loadTwitter();
  }, []);

  return (
    <div className="twitter-embed">
      <TwitterBlockquote className="twitter-timeline tw-align-center">
        <a href={src}>...</a>
      </TwitterBlockquote>
    </div>
  );
}

interface Parser {
  regex: RegExp;
  Component: React.FC<TweetProps>;
}

const srcParsers: Parser[] = [
  {
    regex: /https:\/\/[^/]*twitter.com\/.+?\/status\/[0-9]+/,
    Component: TwitterTweetContainer,
  },
  {
    regex: /https:\/\/x.com\/.+?\/status\/[0-9]+/,
    Component: TwitterTweetContainer,
  },
  {
    regex: /https:\/\/[^/]*twitter.com\/.+?/,
    Component: TwitterTimelineContainer,
  },
  {
    regex: /https:\/\/x.com\/.+?/,
    Component: TwitterTimelineContainer,
  },
  {
    regex: /https:\/\/[^/]*tiktok.com\/.+?\/video\/[0-9+]+/,
    Component: TikTokContainer,
  },
  {
    regex: /https:\/\/[^/]*docs.google.com\/forms\/.*/,
    Component: GoogleDocsContainer,
  },
  {
    regex: /https:\/\/[^/]*bsky.app\/.*/,
    Component: BlueSkyContainer,
  },
];

const SocialEmbed: React.FC<EmbedPluginProps> = (props) => {
  const { src } = props;

  const renderer = srcParsers.find((parser) => {
    return parser.regex.test(src);
  });

  if (!renderer) {
    return (
      <WrapperEmpty>
        <ViewCompact height={60} width={60} color="var(--theme-palette-white)" />
      </WrapperEmpty>
    );
  }

  return (
    <Wrapper>
      <renderer.Component src={src} />
    </Wrapper>
  );
};

export default SocialEmbed;
